/**
 * Carteira: saldo real, identidade, depósito e saque por Pix.
 *
 * A tela tem dois estados, e a diferença entre eles não é cosmética: sem
 * data de nascimento e CPF declarados, o servidor recusa depósito e
 * recusa aposta com valor real. Então ou se pede isso primeiro, ou não há
 * carteira nenhuma para mostrar.
 *
 * O que a tela diz sobre esses dados é a verdade: são declarados, não
 * verificados. Chamar isso de "verificação" seria dar ao jogador a
 * impressão de que alguém conferiu alguma coisa.
 *
 * O saque não tem campo de destino, e a tela diz por quê em vez de deixar
 * a ausência parecer defeito: o dinheiro volta para o CPF do titular, e
 * não há para onde mais mandá-lo. Uma tela que pedisse a chave sugeriria
 * uma escolha que não existe — e sugeriria, a quem quisesse abusar, que
 * vale a pena procurar como forçá-la.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type Cobranca,
  type EstadoConta,
  type EstadoSaque,
  MOTIVOS_SAQUE,
  carregarConta,
  carregarSaque,
  criarDeposito,
  pedirSaque,
  situacaoCobranca,
  verificarIdentidade,
} from '../lib/carteira.ts';
import { EsqueletoCarteira } from '../components/Esqueleto.tsx';
import {
  BarraDeMesa,
  CamadasDeMesa,
  SeloDeMesa,
  corDoJogo,
  molduraDeMesa,
} from '../components/MesaCard.tsx';
import { FormularioIdentidade } from './Identidade.tsx';
import { avisar } from '../lib/avisos.ts';
import { money } from '../lib/format.ts';

/** Valores de depósito oferecidos, em centavos. */
const VALORES = [
  { cents: 2_000, rotulo: 'R$ 20' },
  { cents: 5_000, rotulo: 'R$ 50' },
  { cents: 10_000, rotulo: 'R$ 100' },
  { cents: 20_000, rotulo: 'R$ 200' },
];

const MIN_CENTS = 500;
const MAX_CENTS = 500_000;

/** De quanto em quanto tempo o pagamento é reconsultado. */
const INTERVALO_MS = 4_000;

/**
 * Saldo, no mesmo cartão das mesas.
 *
 * O que justifica o desenho de tabuleiro aqui é o escrow: dinheiro em
 * disputa está, literalmente, numa mesa. Era a linha discreta embaixo do
 * saldo, e é a que mais importa entender — quem vê o total cair sem
 * saber que a diferença está travada numa partida acha que sumiu.
 *
 * Por isso ela ganhou a barra inteira, e diz algo mesmo quando é zero:
 * "nada travado" é informação, não ausência dela.
 *
 * Tema neutro: carteira não é jogo nenhum.
 *
 * Não é botão. O saldo não leva a lugar nenhum — depósito e saque têm
 * seus próprios cartões abaixo.
 */
export function CartaoDeSaldo({
  disponivelCents,
  emDisputaCents,
}: {
  disponivelCents: number;
  emDisputaCents: number;
}) {
  return (
    <section
      className={`${molduraDeMesa({ interativo: false })} min-h-[11rem]`}
      style={{ ['--tema' as string]: corDoJogo('NEUTRO') }}
    >
      <CamadasDeMesa gameType="NEUTRO" />

      <div className="relative flex h-full flex-col justify-between gap-4 p-5 text-white min-h-[11rem]">
        <SeloDeMesa>Sua carteira</SeloDeMesa>

        <div>
          <p className="rotulo" style={{ color: 'rgb(255 255 255 / 0.72)' }}>
            Saldo disponível
          </p>
          <p className="money font-bold tabular-nums" style={{ fontSize: 'var(--text-display)' }}>
            {money(disponivelCents)}
          </p>
        </div>

        <BarraDeMesa comSeta={false}>
          {emDisputaCents > 0 ? (
            <span className="flex items-baseline justify-between gap-3">
              <span>Em disputa numa mesa</span>
              <span className="money tabular-nums shrink-0">{money(emDisputaCents)}</span>
            </span>
          ) : (
            'Nada travado em mesa'
          )}
        </BarraDeMesa>
      </div>
    </section>
  );
}

function Aviso({ tom, children }: { tom: 'erro' | 'ok'; children: React.ReactNode }) {
  return (
    <p
      role={tom === 'erro' ? 'alert' : 'status'}
      className="text-sm font-medium"
      style={{ color: tom === 'erro' ? 'var(--danger)' : 'var(--primary)' }}
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
function Pagamento({
  token,
  cobranca,
  onPago,
  onCancelar,
}: {
  token: string;
  cobranca: Cobranca;
  onPago: () => void;
  onCancelar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);
  const [status, setStatus] = useState('PENDING');
  const [restam, setRestam] = useState(cobranca.expiraSegundos);
  const encerrado = useRef(false);

  useEffect(() => {
    const relogio = setInterval(() => setRestam((s) => Math.max(0, s - 1)), 1_000);

    // O pagamento acontece fora daqui, no aplicativo do banco, e o crédito
    // vem pelo webhook. A tela só pergunta se já chegou.
    const consulta = setInterval(() => {
      if (encerrado.current) return;
      void situacaoCobranca(token, cobranca.txid).then((resultado) => {
        if (resultado.estado !== 'ok') return;
        setStatus(resultado.dado.status);
        if (resultado.dado.status === 'PAID' && !encerrado.current) {
          encerrado.current = true;
          onPago();
        }
      });
    }, INTERVALO_MS);

    return () => {
      clearInterval(relogio);
      clearInterval(consulta);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, cobranca.txid]);

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(cobranca.brCode);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2_000);
    } catch {
      // Sem permissão de área de transferência o código continua à vista,
      // e dá para selecionar à mão. Não vale interromper por isso.
    }
  }

  const minutos = Math.floor(restam / 60);
  const segundos = restam % 60;

  return (
    <div className="card card-destaque p-6 grid gap-4">
      <div>
        <span className="selo">aguardando pagamento</span>
        <h2 className="mt-3 font-bold" style={{ fontSize: 'var(--text-title)' }}>
          {money(cobranca.amountCents)} por Pix
        </h2>
      </div>

      <div className="grid gap-2">
        <span className="rotulo">Copia e cola</span>
        <code
          className="text-xs break-all rounded-lg border p-3 leading-relaxed"
          style={{ background: 'var(--muted)' }}
        >
          {cobranca.brCode}
        </code>
        <button type="button" className="btn btn-primary" onClick={() => void copiar()}>
          {copiado ? 'Copiado' : 'Copiar código'}
        </button>
      </div>

      <ol className="grid gap-1 text-sm text-muted-foreground">
        <li>1. Abra o aplicativo do seu banco.</li>
        <li>2. Escolha Pix, depois Copia e cola.</li>
        <li>3. Cole o código e confirme.</li>
      </ol>

      <p
        className="text-sm font-medium tabular-nums"
        style={{ color: restam <= 60 ? 'var(--danger)' : 'var(--muted-foreground)' }}
      >
        {restam > 0
          ? `Vence em ${minutos}min ${String(segundos).padStart(2, '0')}s`
          : 'Cobrança vencida. Gere outra.'}
      </p>

      <p className="text-xs text-muted-foreground" aria-live="polite">
        {status === 'PENDING'
          ? 'Assim que o banco confirmar, o saldo aparece aqui sozinho — pode deixar esta tela aberta.'
          : `Situação: ${status}`}
      </p>

      <button type="button" className="btn btn-ghost" onClick={onCancelar}>
        Voltar
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------
/**
 * Conferir na Receita uma identidade que já foi declarada.
 *
 * Contas criadas antes de a conferência existir ficam em PENDING sem
 * saída — declarar é uma vez só. Sem este botão, a única forma de sair de
 * lá é alguém da equipe olhar documento, uma conta por vez.
 *
 * Não há campo nenhum de propósito: o CPF conferido é o que já está
 * gravado. Pedir o CPF de novo aqui deixaria a pessoa escolher contra o
 * que a própria conta é verificada.
 */
function VerificarIdentidade({
  token,
  onPronto,
}: {
  token: string;
  onPronto: (conta: EstadoConta) => void;
}) {
  const [ocupado, setOcupado] = useState(false);

  async function conferir(): Promise<void> {
    setOcupado(true);
    const r = await verificarIdentidade(token);
    setOcupado(false);

    if (r.estado !== 'ok') {
      avisar.erro('Não deu para conferir', r.mensagem);
      return;
    }
    onPronto(r.dado);
    avisar.ok('Identidade conferida', 'Seus dados batem com o CPF. O saque está liberado.');
  }

  return (
    <section className="card p-5 grid gap-3">
      <h2 className="font-semibold">Confirmar sua identidade</h2>
      <p className="text-sm text-muted-foreground">
        Seus dados já estão registrados, mas ainda não foram conferidos — e o saque só sai
        depois disso. A conferência é automática e leva alguns segundos.
      </p>
      <button
        type="button"
        className="btn btn-primary justify-self-start"
        onClick={() => void conferir()}
        disabled={ocupado}
      >
        {ocupado ? 'Conferindo…' : 'Conferir agora'}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------
/**
 * Saque.
 *
 * Repare no que não existe: campo de destino. O dinheiro vai para a
 * chave-CPF do próprio titular, e a tela mostra o CPF mascarado só para a
 * pessoa reconhecer para onde vai — não para escolher.
 */
function Saque({ token, onPedido }: { token: string; onPedido: () => void }) {
  const [estado, setEstado] = useState<EstadoSaque | null>(null);
  const [reais, setReais] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const recarregar = useCallback(async (): Promise<void> => {
    const resultado = await carregarSaque(token);
    if (resultado.estado === 'ok') setEstado(resultado.dado);
  }, [token]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  if (!estado) return null;

  const emAndamento = estado.emAndamento;
  if (emAndamento) {
    return (
      <section className="card p-5 grid gap-2">
        <h2 className="rotulo">Saque em andamento</h2>
        <div className="flex items-baseline justify-between gap-4">
          <span className="money font-bold" style={{ fontSize: 'var(--text-title)' }}>
            {money(emAndamento.amount_cents)}
          </span>
          <span className="text-sm text-muted-foreground">
            {emAndamento.status === 'REQUESTED'
              ? 'Aguardando conferência'
              : emAndamento.status === 'APPROVED'
                ? 'Aprovado, indo para o banco'
                : 'Enviado ao banco'}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          O valor já saiu do seu saldo disponível. Se o banco recusar o envio, ele volta
          para a sua carteira.
        </p>
      </section>
    );
  }

  if (!estado.podeSacar) {
    return (
      <section className="card p-5 grid gap-2">
        <h2 className="rotulo">Saque</h2>
        <p className="text-sm text-muted-foreground">
          {MOTIVOS_SAQUE[estado.motivo ?? ''] ?? 'Saque indisponível no momento.'}
        </p>
      </section>
    );
  }

  const digitado = reais.trim() === '' ? null : Math.round(Number(reais.replace(',', '.')) * 100);
  const teto = Math.min(estado.disponivelCents, estado.limites.maximoCents);
  const valido =
    digitado !== null &&
    Number.isInteger(digitado) &&
    digitado >= estado.limites.minimoCents &&
    digitado <= teto;

  async function sacar(): Promise<void> {
    if (digitado === null) return;
    setEnviando(true);
    setErro(null);
    const resultado = await pedirSaque(token, digitado);
    setEnviando(false);

    if (resultado.estado === 'ok') {
      setReais('');
      await recarregar();
      onPedido();
      return;
    }
    setErro(resultado.mensagem);
  }

  return (
    <section className="card p-5 grid gap-4">
      <h2 className="rotulo">Sacar por Pix</h2>

      <label className="grid gap-1.5">
        <span className="rotulo">Valor (R$)</span>
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min={estado.limites.minimoCents / 100}
          max={teto / 100}
          value={reais}
          onChange={(e) => setReais(e.target.value)}
          placeholder={(estado.limites.minimoCents / 100).toFixed(2)}
          className="w-40 rounded-lg border px-4 py-2.5 outline-none transition"
          style={{ background: 'var(--card)' }}
        />
      </label>

      <p className="text-xs text-muted-foreground">
        Entre {money(estado.limites.minimoCents)} e {money(teto)}.
      </p>

      {estado.destinoMascarado ? (
        <div className="grid gap-1 border-t pt-3">
          <span className="rotulo">Destino</span>
          <span className="text-sm">Chave Pix CPF {estado.destinoMascarado}</span>
          <p className="text-xs text-muted-foreground">
            O saque vai sempre para o CPF da sua conta. Não dá para escolher outra chave —
            é assim que garantimos que o dinheiro só volta para quem é dono dele.
          </p>
        </div>
      ) : null}

      {erro ? <Aviso tom="erro">{erro}</Aviso> : null}

      <button
        type="button"
        className="btn btn-primary !py-3"
        disabled={enviando || !valido}
        onClick={() => void sacar()}
      >
        {enviando ? 'Enviando pedido…' : `Sacar ${digitado === null ? '' : money(digitado)}`}
      </button>

      <p className="text-xs text-muted-foreground">
        O valor sai do saldo assim que você pede. Um responsável confere antes de o Pix ser
        enviado, e o saque só é concluído quando o banco confirma.
      </p>
    </section>
  );
}

export function Carteira({ token, onVoltar }: { token: string; onVoltar: () => void }) {
  const [conta, setConta] = useState<EstadoConta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [valor, setValor] = useState<number>(VALORES[1]?.cents ?? 5_000);
  const [reais, setReais] = useState('');
  const [cobranca, setCobranca] = useState<Cobranca | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Consultas em voo se atropelam: a tela recarrega ao abrir, ao declarar
  // identidade e ao confirmar pagamento. Sem ordem, uma resposta lenta e
  // antiga sobrescreve a nova, e o jogador vê um erro que já não existe
  // por cima do saldo que acabou de entrar.
  const consulta = useRef(0);

  const recarregar = useCallback(async (): Promise<void> => {
    const minha = ++consulta.current;
    const resultado = await carregarConta(token);
    if (minha !== consulta.current) return;

    setCarregando(false);
    if (resultado.estado === 'ok') {
      setConta(resultado.dado);
      setErro(null);
      return;
    }
    setErro(resultado.mensagem);
  }, [token]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const digitado = reais.trim() === '' ? null : Math.round(Number(reais.replace(',', '.')) * 100);
  const escolhido = digitado ?? valor;
  const valorValido =
    Number.isInteger(escolhido) && escolhido >= MIN_CENTS && escolhido <= MAX_CENTS;

  async function depositar(): Promise<void> {
    setEnviando(true);
    setErro(null);
    const resultado = await criarDeposito(token, escolhido);
    setEnviando(false);

    if (resultado.estado === 'ok') {
      setCobranca(resultado.dado);
      return;
    }
    setErro(resultado.mensagem);
  }

  if (carregando) return <EsqueletoCarteira />;

  if (!conta) {
    return (
      <main className="grow grid place-items-center px-4">
        <div className="card p-6 max-w-md w-full grid gap-4 text-center">
          <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
            Carteira indisponível
          </h1>
          <p className="text-muted-foreground">{erro ?? 'Não deu para carregar.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => void recarregar()}>
            Tentar de novo
          </button>
          <button type="button" className="btn btn-ghost" onClick={onVoltar}>
            Voltar ao salão
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8 grid gap-6 content-start grow">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
          Carteira
        </h1>
        <button type="button" className="btn btn-ghost !px-3 !py-1.5" onClick={onVoltar}>
          Voltar ao salão
        </button>
      </div>

      <CartaoDeSaldo
        disponivelCents={conta.balance.availableCents}
        emDisputaCents={conta.balance.escrowCents}
      />

      {conta.identityDeclared && conta.kycStatus === 'PENDING' ? (
        <VerificarIdentidade token={token} onPronto={setConta} />
      ) : null}

      {!conta.identityDeclared ? (
        <FormularioIdentidade
          token={token}
          titulo="Antes de depositar"
          explicacao="Partidas com valor real exigem 18 anos, e o CPF é o que identifica o pagador na cobrança Pix."
          onPronto={(nova) => {
            setConta(nova);
            avisar.ok('Dados registrados', 'Você já pode depositar.');
          }}
        />
      ) : cobranca ? (
        <Pagamento
          token={token}
          cobranca={cobranca}
          onPago={() => {
            setCobranca(null);
            avisar.ok('Pagamento confirmado', 'O saldo já está na sua conta.');
            void recarregar();
          }}
          onCancelar={() => setCobranca(null)}
        />
      ) : !conta.canUseRealMoney ? (
        <div className="card p-5 grid gap-3">
          <h2 className="font-semibold">Depósito indisponível</h2>
          <p className="text-muted-foreground text-sm">
            {conta.blockedReason === 'SELF_EXCLUDED'
              ? `Sua conta está em autoexclusão${
                  conta.selfExcludedUntil
                    ? ` até ${new Date(conta.selfExcludedUntil).toLocaleDateString('pt-BR')}`
                    : ''
                }.`
              : 'Sua conta não está liberada para dinheiro real. Fale com o suporte.'}
          </p>
        </div>
      ) : (
        <section className="card p-5 grid gap-4">
          <h2 className="rotulo">Depositar por Pix</h2>

          <ul className="flex flex-wrap gap-2" role="radiogroup" aria-label="Valor do depósito">
            {VALORES.map((v) => (
              <li key={v.cents}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={digitado === null && valor === v.cents}
                  onClick={() => {
                    setValor(v.cents);
                    setReais('');
                  }}
                  className={[
                    'btn',
                    digitado === null && valor === v.cents ? 'btn-primary' : 'btn-ghost',
                  ].join(' ')}
                >
                  {v.rotulo}
                </button>
              </li>
            ))}
          </ul>

          <label className="grid gap-1.5">
            <span className="rotulo">Outro valor (R$)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min={MIN_CENTS / 100}
              max={MAX_CENTS / 100}
              value={reais}
              onChange={(e) => setReais(e.target.value)}
              placeholder={(MIN_CENTS / 100).toFixed(2)}
              className="w-40 rounded-lg border px-4 py-2.5 outline-none transition"
              style={{ background: 'var(--card)' }}
            />
          </label>

          {conta.dailyDepositLimitCents !== null ? (
            <p className="text-xs text-muted-foreground">
              Seu limite diário de depósito é {money(conta.dailyDepositLimitCents)}.
            </p>
          ) : null}

          {erro ? <Aviso tom="erro">{erro}</Aviso> : null}

          <button
            type="button"
            className="btn btn-primary !py-3"
            disabled={enviando || !valorValido}
            onClick={() => void depositar()}
          >
            {enviando ? 'Gerando cobrança…' : `Depositar ${money(escolhido)}`}
          </button>

          <p className="text-xs text-muted-foreground">
            O saldo entra quando o banco confirma o pagamento, não quando a cobrança é gerada.
          </p>
        </section>
      )}

      {conta.identityDeclared && !cobranca ? (
        <Saque
          token={token}
          onPedido={() => {
            avisar.ok(
              'Pedido de saque registrado',
              'O valor já saiu do saldo disponível.',
            );
            void recarregar();
          }}
        />
      ) : null}
    </main>
  );
}
